import { useState } from "react";
import { Flag, X } from "lucide-react";
import { api } from "../lib/api";

/** Inline button that opens a modal to report an error for a specific field on a waterfall. */
export default function ReportErrorButton({ slug, field, fieldLabel }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { setErr("Please describe the issue."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/error-reports", {
        waterfall_slug: slug,
        field,
        message: text.trim(),
        reporter_email: email.trim() || undefined,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); setDone(false); setText(""); setErr(""); }}
        data-testid={`report-btn-${field}`}
        title="Report an error in this information"
        className="ml-1 inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-rose-600 font-medium leading-none align-middle"
      >
        <Flag className="w-3 h-3" /> Report
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid={`report-modal-${field}`}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
              <h3 className="text-base font-extrabold text-stone-900">
                Report an error
              </h3>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="report-modal-close"
                className="text-stone-500 hover:text-stone-900"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {done ? (
              <div className="p-6 text-center" data-testid="report-success">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                  <Flag className="w-5 h-5 text-emerald-700" />
                </div>
                <p className="text-stone-900 font-semibold mb-1">Thanks for the report</p>
                <p className="text-sm text-stone-600 mb-5">We'll review it and update the page if needed.</p>
                <button
                  onClick={() => setOpen(false)}
                  className="btn-at btn-at-green !px-5"
                  data-testid="report-success-close"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-3">
                <p className="text-xs text-stone-500">
                  Field: <span className="font-semibold text-stone-700">{fieldLabel}</span>
                </p>
                <textarea
                  data-testid="report-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="What's wrong or out of date? Include a source if you have one."
                  rows={5}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  maxLength={2000}
                />
                <input
                  data-testid="report-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email (optional, so we can follow up)"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600"
                />
                {err && <p className="text-xs text-rose-600" data-testid="report-error">{err}</p>}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 rounded-full text-sm font-semibold text-stone-700 hover:bg-stone-100"
                    data-testid="report-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={busy || !text.trim()}
                    data-testid="report-submit"
                    className="btn-at btn-at-green disabled:opacity-50 disabled:cursor-not-allowed !px-5"
                  >
                    {busy ? "Submitting…" : "Submit report"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
